import { computeAgeYears, roundToNearest } from './gvMath';
import { MANUFACTURERS } from '../config/aadConfig';

const getCypresServiceLife = (domYear, rules) => {
  for (const rule of rules) {
    if (rule.dom_up_to != null && domYear <= rule.dom_up_to) return rule.years;
    if (rule.dom_from != null && domYear >= rule.dom_from) return rule.years;
  }
  return Math.min(...rules.map(r => r.years));
};

const getVigilBatteryLife = (domYear, batteryCfg) => {
  if (domYear <= 2018) return batteryCfg.life_years_dom_up_to_2018;
  return batteryCfg.life_years_dom_from_2019;
};

export const calculateAAD = ({ manufacturer, model, domYear, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrKey;
  let mfrCfg;
  let modelCfg;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    notes.push('Custom price used. Brand and version adjustments not applied.');
    // Need manufacturer to determine service life rules
    mfrKey = manufacturer ? manufacturer.toLowerCase().trim() : null;
    mfrCfg = mfrKey ? MANUFACTURERS[mfrKey] : null;
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
  } else {
    mfrKey = manufacturer.toLowerCase().trim();
    const mdl = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    modelCfg = mfrCfg.models[mdl];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
  }

  // Service life
  let totalLife;
  if (mfrKey === 'cypres') {
    totalLife = getCypresServiceLife(domYear, mfrCfg.service_life);
  } else {
    totalLife = mfrCfg.service_life_years;
  }

  // Age and remaining life
  const ageYears = computeAgeYears(domYear);
  const remainingLife = totalLife - ageYears;

  adjustments.totalLifeYears = totalLife;
  adjustments.remainingLifeYears = Math.max(0, Math.round(remainingLife * 10) / 10);

  // Expired
  if (remainingLife <= 0) {
    let floor = 0;
    if (mfrKey === 'cypres') {
      floor = mfrCfg.trade_in_value_eur || 0;
      notes.push(`Expired. Trade-in value only: ${floor} EUR`);
    } else {
      notes.push('Expired. No residual value.');
    }
    return {
      valueEur: floor,
      newPriceEur: Math.round(newPriceEur * 100) / 100,
      retentionPct: newPriceEur > 0 ? Math.round((floor / newPriceEur) * 1000) / 10 : 0,
      notes,
      adjustments,
    };
  }

  // Linear depreciation
  let valueEur = newPriceEur * (remainingLife / totalLife);
  adjustments.ageFactor = Math.round((remainingLife / totalLife) * 1000) / 1000;

  // Vigil battery deduction
  if (mfrKey === 'vigil') {
    const batteryCfg = mfrCfg.battery;
    const batteryLife = getVigilBatteryLife(domYear, batteryCfg);
    if (ageYears >= batteryLife) {
      const batteryCostEur = batteryCfg.replacement_cost_eur;
      valueEur -= batteryCostEur;
      notes.push(`Battery replacement due. Deducted ${Math.round(batteryCostEur)} EUR.`);
      adjustments.batteryDeduction = batteryCostEur;
    }
  }

  // Floor at zero
  valueEur = Math.max(valueEur, 0);

  // Round to nearest 5
  valueEur = roundToNearest(valueEur, 5);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
